import { AccountingReportCycleTotal } from "@common/constants";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_COMPARISON_OPTIONS,
} from "@common/types/reports/report-type";
import { REPORT_PACKAGE_CYCLE_SUPPORTED } from "@common/types/reports/report-package";
import { HStack } from "@design-system/HStack/HStack";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { AccountingReportComparisonSelect } from "../AccountingReportComparisonSelect";
import { AccountingReportCycleSelect } from "../AccountingReportCycleSelect";
import { SchedulesReportRowsSelect } from "../schedulesReport/SchedulesReportRowsSelect";
import { RevenueSubModeSelect } from "../schedulesReport/RevenueSubModeSelect";
import { RecognitionMethodSelect } from "../schedulesReport/RecognitionMethodSelect";
import {
    resolveCycleChange,
    buildSchedulesReportLayoutParams,
} from "../schedulesReport/schedulesReportFilterRules";
import {
    DEFAULT_SCHEDULES_REPORT_LAYOUT,
    DEFAULT_SCHEDULES_REPORT_ROWS,
    DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE,
    DEFAULT_SCHEDULES_REPORT_METHOD,
} from "../schedulesReport/useSchedulesReportParams";
import {
    ReportPackageRow,
    ReportPackageRowOptions,
    reportPackageSupportsClassCycle,
} from "./reportPackageRows.lib";
import styles from "./ReportPackageBuilderDrawer.module.scss";

interface Props {
    row: ReportPackageRow;
    canGroupByClass: boolean;
    onOptionsChange: (options: ReportPackageRowOptions) => void;
    onUpdate: (patch: Partial<Omit<ReportPackageRow, "id" | "title">>) => void;
}

export const ReportPackageBuilderRowOptions = ({
    row,
    canGroupByClass,
    onOptionsChange,
    onUpdate,
}: Props) => {
    const isSchedules = row.reportType === AccountingReportType.SCHEDULES;
    const layout = row.layout ?? DEFAULT_SCHEDULES_REPORT_LAYOUT;
    const { schedulesMilestoneBased: isMilestoneEnabled } = useTypedFlags();
    const comparisonOptions =
        ACCOUNTING_REPORT_COMPARISON_OPTIONS[row.reportType];
    const hasCycleSupport =
        isSchedules || REPORT_PACKAGE_CYCLE_SUPPORTED.has(row.reportType);
    const hasComparisonSupport = comparisonOptions.comparisons.length > 0;
    if (!hasCycleSupport && !hasComparisonSupport) {
        return null;
    }
    return (
        <HStack gap={8} justify="end" wrap>
            {hasCycleSupport && (
                <AccountingReportCycleSelect
                    value={row.cycle ?? AccountingReportCycleTotal.TOTAL}
                    onChange={(cycle) => {
                        const nextCycle =
                            cycle === AccountingReportCycleTotal.TOTAL
                                ? undefined
                                : cycle;
                        if (isSchedules) {
                            const next = resolveCycleChange({
                                layout,
                                nextCycle,
                            });
                            onUpdate({
                                ...buildSchedulesReportLayoutParams({
                                    layout: next.layout,
                                    rowsMode:
                                        row.rows ??
                                        DEFAULT_SCHEDULES_REPORT_ROWS,
                                }),
                                cycle: next.cycle,
                            });
                        } else {
                            onOptionsChange({ cycle: nextCycle });
                        }
                    }}
                    showClassOption={
                        canGroupByClass &&
                        reportPackageSupportsClassCycle(row.reportType)
                    }
                    className={styles.optionTrigger}
                />
            )}
            {isSchedules && (
                <>
                    <SchedulesReportRowsSelect
                        value={row.rows ?? DEFAULT_SCHEDULES_REPORT_ROWS}
                        layout={layout}
                        onChange={(rows) => onUpdate({ rows })}
                        className={styles.optionTrigger}
                    />
                    <RevenueSubModeSelect
                        value={
                            row.revenueSubMode ??
                            DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE
                        }
                        onChange={(revenueSubMode) =>
                            onUpdate({ revenueSubMode })
                        }
                    />
                    <RecognitionMethodSelect
                        value={row.method ?? DEFAULT_SCHEDULES_REPORT_METHOD}
                        isMilestoneEnabled={isMilestoneEnabled}
                        onChange={(method) => onUpdate({ method })}
                    />
                </>
            )}
            {hasComparisonSupport && (
                <AccountingReportComparisonSelect
                    comparisons={row.comparisons ?? []}
                    comparisonDiffs={row.comparisonDiffs ?? []}
                    ratioTypes={row.ratioTypes ?? []}
                    availableOptions={comparisonOptions}
                    onChange={onOptionsChange}
                    className={styles.optionTrigger}
                />
            )}
        </HStack>
    );
};
